import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function JoinTeam() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token."); return; }
    api.get(`/team/invites/${token}`).then((r) => setInvite(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/team/accept", { token });
      await refresh();
      toast.success("Welcome to the team");
      navigate("/dashboard");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const next = encodeURIComponent(`/join-team?token=${token}`);

  return (
    <div className="max-w-lg mx-auto px-4 py-20" data-testid="join-team-page">
      <div className="bg-card border border-border rounded-2xl p-8 shadow-sm text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Users className="w-7 h-7 text-primary" /></div>
        {error ? (
          <>
            <h1 className="font-display text-xl font-bold mb-2">Invitation problem</h1>
            <p className="text-sm text-destructive mb-6" data-testid="join-team-error">{error}</p>
            <Button asChild variant="outline"><Link to="/dashboard">Go to dashboard</Link></Button>
          </>
        ) : !invite ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /> : (
          <>
            <h1 className="font-display text-xl font-bold mb-2">Join {invite.org_name || "the team"}</h1>
            <p className="text-sm text-muted-foreground mb-6">{invite.inviter || "A colleague"} invited <strong>{invite.email}</strong> to share their Enterprise subscription on Futuregen Innovations.</p>
            {user ? (
              <Button onClick={accept} disabled={busy} data-testid="join-team-accept-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept invitation</>}
              </Button>
            ) : (
              <div className="flex gap-2 justify-center flex-wrap">
                <Button asChild data-testid="join-team-login-link"><Link to={`/login?next=${next}`}>Sign in to accept</Link></Button>
                <Button asChild variant="outline"><Link to={`/register?next=${next}`} state={{ email: invite.email }}>Create account</Link></Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
